/**
 * VRG-464 — client-side staleness for a market citation (VRG-445 AC2/AC4).
 *
 * "An answer citing a two-year-old figure says so" is the epic's own example, and it is
 * the UI's job, never the model's: the system prompt (ask.service.ts) does not mention
 * age at all, so a model cannot forget to bring it up — there is nothing to forget.
 * Every market citation carries `publishedAt` (the ARTICLE's own publish date), and this
 * file turns that into the label + a stale/fresh classification the renderer uses.
 */
import type { AskMarketCitation } from './types'

/**
 * The threshold at which a figure is flagged as possibly outdated — two years, matching
 * VRG-445's own worked example ("an answer citing a two-year-old figure says so"). Not a
 * hard cutoff on WHETHER to show the date (every market citation always shows its date);
 * it only changes how loudly the age is called out.
 */
const STALE_THRESHOLD_DAYS = 365 * 2

const MS_PER_DAY = 24 * 60 * 60 * 1000

export interface MarketCitationAge {
  /** e.g. "3 months ago", "2 years ago". Null when no publish date is on record. */
  label: string | null
  /** True once the figure is older than STALE_THRESHOLD_DAYS. Always false with no date. */
  isStale: boolean
}

/** Human-readable relative age, coarse on purpose — "3 months ago", not "94 days ago". */
const relativeAgeLabel = (days: number): string => {
  if (days < 1) return 'today'
  if (days < 31) return days === 1 ? '1 day ago' : `${days} days ago`
  const months = Math.round(days / 30.44)
  if (months < 24) return months <= 1 ? '1 month ago' : `${months} months ago`
  const years = Math.round(days / 365.25)
  return years <= 1 ? '1 year ago' : `${years} years ago`
}

/**
 * `publishedAt` is an ISO string (server-serialised `market_items.published_at`), or
 * null when the source article's own publish date was never recorded at ingest.
 */
export const describeMarketCitationAge = (
  publishedAt: string | null,
  now: Date = new Date(),
): MarketCitationAge => {
  if (!publishedAt) return { label: null, isStale: false }
  const published = new Date(publishedAt)
  if (Number.isNaN(published.getTime())) return { label: null, isStale: false }

  const days = Math.max(0, Math.floor((now.getTime() - published.getTime()) / MS_PER_DAY))
  return {
    label: relativeAgeLabel(days),
    isStale: days > STALE_THRESHOLD_DAYS,
  }
}

/** Convenience wrapper reading straight off a citation. */
export const describeCitationAge = (
  citation: AskMarketCitation,
  now: Date = new Date(),
): MarketCitationAge => describeMarketCitationAge(citation.publishedAt, now)
